import {
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
} from "@dnd-kit/core";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";
import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { DNDContext } from "../../../components/dnd/DNDContext";
import useIsMobileViewport from "../../../hooks/useIsMobileViewport";
import i18n from "../../../i18n";
import { KanbanAutomationRule } from "../../../model/kanban/KanbanAutomationRule";
import { KanbanColumn } from "../../../model/kanban/KanbanColumn";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { User } from "../../../model/user/User";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useDrawersContext } from "../../tasks/drawers-context";
import {
  getTranslatedKanbanColumnName,
  getTranslatedKanbanName,
} from "../KanbanUtils";
import { KanbanAutomationLane } from "./KanbanAutomationLane";
import { KanbanAutomationRuleCard } from "./KanbanAutomationRuleCard";
import { KanbanAutomationRuleDeleteModal } from "./KanbanAutomationRuleDeleteModal";
import { KanbanAutomationRuleDrawer } from "./KanbanAutomationRuleDrawer";

const DRAWER_SIZE_DESKTOP = "min(96vw, 1800px)";
const DRAWER_SIZE_MOBILE = "100%";
/** Above the drawers (2000/2001) the overlay is dragged over. */
const DRAG_OVERLAY_Z_INDEX = 2100;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.m};
  height: 100%;
  min-height: 24rem;
`;

// Laid out like the board: a left-to-right scroller whose lanes are reversed
// for Arabic, so the first column is on the right and the drag auto-scroll
// keeps working.
const LanesScroller = styled.div<{ $loading: boolean }>`
  direction: ltr;
  display: flex;
  flex: 1;
  gap: ${Spacing.l};
  min-height: 0;
  padding-bottom: ${Spacing.s};
  overflow-x: auto;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? "none" : "auto")};

  scrollbar-color: ${({ theme }) => `${theme.main} ${theme.sub}`};
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.sub};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.main};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.lightMain};
  }
`;

type RuleFormTarget = {
  columnId: string;
  columnName: string;
  /** `null` while configuring a new rule. */
  rule: KanbanAutomationRule | null;
};

export const KanbanAutomationDrawer: React.FC = () => {
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  // Kept after the rule drawer closes so its content does not change while it
  // slides out.
  const [ruleFormTarget, setRuleFormTarget] = useState<RuleFormTarget | null>(
    null,
  );
  const [ruleToDelete, setRuleToDelete] = useState<KanbanAutomationRule | null>(
    null,
  );
  const [draggedRule, setDraggedRule] = useState<KanbanAutomationRule | null>(
    null,
  );
  // rule id -> the lane it was just dropped into, until the move is confirmed.
  const [pendingMoves, setPendingMoves] = useState<Record<string, string>>({});

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileViewport();

  const { kanbanAutomationVisible, setKanbanAutomationVisible } =
    useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const kanbanLoading = useAppSelector(
    kanbanSliceSelectors.selectUserKanbanLoading,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const isRtl = i18n.language === AppLangs.AR;

  const columns = useMemo(() => kanban?.columns ?? [], [kanban?.columns]);

  const allRules = useMemo(
    () => columns.flatMap(({ automationRules }) => automationRules ?? []),
    [columns],
  );

  // The rules of each lane, oldest first. A rule that was just dropped in
  // another lane already shows there while its move is being saved.
  const rulesByColumnId = useMemo(() => {
    const byColumnId: Record<string, KanbanAutomationRule[]> = {};

    columns.forEach(({ _id }) => {
      byColumnId[_id] = [];
    });

    columns.forEach((column) =>
      column.automationRules?.forEach((rule) =>
        (byColumnId[pendingMoves[rule._id]] ?? byColumnId[column._id]).push(
          rule,
        ),
      ),
    );

    return mapValues(byColumnId, (rules) => sortBy(rules, "createdAt"));
  }, [columns, pendingMoves]);

  // Everyone a rule card can name: its recipients and whoever added it.
  const usersById = useMemo(() => {
    const byId = new Map<string, User>();

    [user?.organization, user, ...members].forEach((member) => {
      if (member) {
        byId.set(member._id, member);
      }
    });

    return byId;
  }, [user, members]);

  const getColumnName = (column: KanbanColumn) =>
    getTranslatedKanbanColumnName(column.name, kanban?.template) || "";

  const getColumnNameById = (columnId: string) => {
    const column = columns.find(({ _id }) => _id === columnId);

    return column ? getColumnName(column) : "";
  };

  const onClose = () => {
    setKanbanAutomationVisible(false);
    setRuleFormOpen(false);
  };

  const onAddRule = (column: KanbanColumn) => {
    setRuleFormTarget({
      columnId: column._id,
      columnName: getColumnName(column),
      rule: null,
    });
    setRuleFormOpen(true);
  };

  const onEditRule = (rule: KanbanAutomationRule) => {
    setRuleFormTarget({
      columnId: rule.parentId,
      columnName: getColumnNameById(rule.parentId),
      rule,
    });
    setRuleFormOpen(true);
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setDraggedRule(allRules.find(({ _id }) => _id === active.id) ?? null);
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setDraggedRule(null);

    const rule = allRules.find(({ _id }) => _id === active.id);
    const destinationColumnId = over ? String(over.id) : undefined;

    // Dropped outside every lane, or back in the lane it came from.
    if (
      !rule ||
      !destinationColumnId ||
      destinationColumnId === rule.parentId
    ) {
      return;
    }

    try {
      setPendingMoves((moves) => ({
        ...moves,
        [rule._id]: destinationColumnId,
      }));

      await dispatch(
        kanbanActions.updateAutomationRule({
          userId,
          kanbanId: kanban!._id,
          ruleId: rule._id,
          columnId: destinationColumnId,
        }),
      ).unwrap();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      // On success the store already holds the rule in its new lane; on
      // failure dropping the override is what puts it back.
      setPendingMoves((moves) => omit(moves, rule._id));
    }
  };

  // Puts the lanes' scroll at the first column, which is the right-hand end in
  // Arabic.
  const scrollToFirstColumn = useCallback((scroller: HTMLDivElement | null) => {
    if (scroller && i18n.language === AppLangs.AR) {
      scroller.scrollLeft = scroller.scrollWidth;
    }
  }, []);

  const renderBoard = () => {
    if (!kanban || (kanbanLoading && !columns.length)) {
      return <SpinnerFullScreen containerHeight="100%" />;
    }

    if (!columns.length) {
      return (
        <AtiraEmpty
          icon={faBolt}
          description={t("kanban.automation.drawer.empty")}
        />
      );
    }

    return (
      <DNDContext
        // A drop counts for the lane under the pointer, so letting go outside
        // every lane cancels the move.
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDraggedRule(null)}
      >
        <LanesScroller ref={scrollToFirstColumn} $loading={kanbanLoading}>
          {(isRtl ? [...columns].reverse() : columns).map((column) => (
            <KanbanAutomationLane
              key={column._id}
              column={column}
              template={kanban.template}
              rules={rulesByColumnId[column._id] ?? []}
              usersById={usersById}
              pendingRuleIds={Object.keys(pendingMoves)}
              onAdd={onAddRule}
              onEdit={onEditRule}
              onDelete={setRuleToDelete}
            />
          ))}
        </LanesScroller>

        {createPortal(
          <DragOverlay zIndex={DRAG_OVERLAY_Z_INDEX} dropAnimation={null}>
            {draggedRule ? (
              <KanbanAutomationRuleCard
                rule={draggedRule}
                columnName={getColumnNameById(draggedRule.parentId)}
                usersById={usersById}
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DNDContext>
    );
  };

  return (
    <>
      <Drawer
        open={kanbanAutomationVisible}
        onClose={onClose}
        title={t("kanban.automation.drawer.title", {
          name: getTranslatedKanbanName(kanban?.name || ""),
        })}
        size={isMobile ? DRAWER_SIZE_MOBILE : DRAWER_SIZE_DESKTOP}
        // Escape cancels a drag in progress - it must not also close the drawer.
        keyboard={!draggedRule}
        destroyOnHidden
      >
        <Content>
          <Info info={t("kanban.automation.drawer.description")} />

          <Flex flex={1} minHeight={0} flexDirection="column">
            {renderBoard()}
          </Flex>
        </Content>
      </Drawer>

      <KanbanAutomationRuleDrawer
        open={ruleFormOpen}
        onClose={() => setRuleFormOpen(false)}
        columnId={ruleFormTarget?.columnId ?? ""}
        columnName={ruleFormTarget?.columnName ?? ""}
        rule={ruleFormTarget?.rule ?? null}
      />

      <KanbanAutomationRuleDeleteModal
        rule={ruleToDelete}
        onClose={() => setRuleToDelete(null)}
      />
    </>
  );
};
